import React, {forwardRef, useContext, useEffect, useMemo, useRef, useState} from 'react';
import * as d3 from 'd3'
import * as d3_dag from 'd3-dag'
import _ from "lodash"
import DialogContent from "@material-ui/core/DialogContent";
import Dialog from "@material-ui/core/Dialog";
import GraphEventDetail from "../../../components/events/GraphEventDetail";
import Grid from "@material-ui/core/Grid";
import SgtinListDownloadComponent from "../SgtinListDownloadComponent";
import {ProductHistoryContext, ThemeContext} from "../../../contexts";
import {extractProductClassCode, findCodeTypeAndValue} from "../../../utils/CodesUtils";
import {Backdrop, Typography} from "@material-ui/core";
import i18next from "../../../i18n/i18next";
import chroma from 'chroma-js'
import NodeDetail from "../sankey_graph/NodeDetail";
import axios from "axios";
import {extractSerialCode} from "../../../utils/Utils";
import CustomLoader from "../../../components/CustomLoader";

//const t = () => new Date().getTime();

export function wrap(text, width) {
    text.each(function () {
        let text = d3.select(this),
            words = text.text().split(/\s+/).reverse(),
            word,
            line = [],
            lineNumber = 0,
            lineHeight = 1.1, // ems
            x = text.attr("x"),
            y = text.attr("y"),
            dy = 0, //parseFloat(text.attr("dy")),
            tspan = text.text(null)
                .append("tspan")
                .attr("x", x)
                .attr("y", y)
                .attr("dy", dy + "em");
        while (word = words.pop()) {
            line.push(word);
            tspan.text(line.join(" "));
            if (tspan.node().getComputedTextLength() > width) {
                line.pop();
                tspan.text(line.join(" "));
                line = [word];
                tspan = text.append("tspan")
                    .attr("x", x)
                    .attr("y", y)
                    .attr("dy", ++lineNumber * lineHeight + dy + "em")
                    .text(word);
            }
        }
    });
}

const renderGraph = (ref, realData, selectedSgtins, maxChildren, gtinNameLUT, w, h, handleIconsClick, handleSelection, primaryColor, accentColor) => {
    d3.select(ref).selectAll("svg").remove()

    let visitedClass = {}

    // extend javascript array class by a remove function
    // copied from https://stackoverflow.com/a/3955096/12267732
    Array.prototype.remove = function () {
        var what, a = arguments, L = a.length, ax;
        while (L && this.length) {
            what = a[--L];
            while ((ax = this.indexOf(what)) !== -1) {
                this.splice(ax, 1);
            }
        }
        return this;
    };

    /**
     * Set the dimensions and margins of the diagram
     * var screen_width = document.body.offsetWidth,
        screen_height = document.documentElement.clientHeight;*/

    // append the svg object to the body of the page
    // assigns width and height
    // activates zoom/pan and tooltips
    const svg = d3.select(ref).append("svg")
        .attr("width", w)
        .attr("height", h)

    // append group element
    const g = svg.append("g");

    // initialize panning, zooming
    var zoom = d3.zoom()
        .on("zoom", (event) => g.attr("transform", event.transform));

    svg.call(zoom)

    // helper variables
    var i = 0,
        duration = 750,
        x_sep = 550,
        y_sep = 150;

    // declare a dag layout
    var tree = d3_dag.sugiyama()
        .nodeSize([y_sep, x_sep])
        //.layering(d3_dag.layeringSimplex())
        .layering(d3_dag.layeringLongestPath())
        //.decross(d3_dag.decrossOpt)
        .decross(d3_dag.twolayerMean)
        //.coord(d3_dag.coordVert())
        .coord(d3_dag.coordCenter())
        .separation(
            (a, b) => { return 1 }
        );

    // make dag from edge list
    let dag = d3_dag.dagStratify()(realData)
    //let dag = d3_dag.dagHierarchy()(realData)

    // in order to make the family tree work, the dag
    // must be a node with id undefined. create that node if
    // not done automatically
    let root
    if(dag.id !== undefined) {
        root = dag.copy();
        root.id = undefined;
        root.children = [dag];
        dag = root;
    }

    // prepare node data
    var all_nodes = dag.descendants()
    all_nodes.forEach(n => {
        const prodClass = extractProductClassCode(_.split(n.id, ',')[0])
        let marker = false
        if (!visitedClass[prodClass + (n.data.originInput || '')]) {
            marker = true
            visitedClass[prodClass + (n.data.originInput || '')] = 1
        }
        //n.data = realData;
        n.marker = marker
        n._children = n.children; // all nodes collapsed by default
        n.children = [];
        n.inserted_nodes = [];
        n.inserted_roots = [];
        n.neighbors = [];
        n.visible = false;
        n.inserted_connections = [];
        n._collapsed = true;
    });

    //console.log("all_nodes ---> dag.descendants(): ", all_nodes)

    // find root node and assign data
    root = all_nodes.find(n => n.parentIds === undefined);
    root.visible = true;
    root.neighbors = getNeighbors(root);
    root.x0 = h / 4;
    root.y0 = -x_sep / 2;

    // overwrite dag root nodes
    dag.children = [root];

    all_nodes.forEach((node) => {
        uncollapse(node)
    })

    // draw dag
    update(root);

    /** // collapse a node
    function collapse(d) {
        //console.log("collapse ---> d:",d)
        // remove root nodes and circle-connections
        var remove_inserted_root_nodes = n => {
            // remove all inserted root nodes
            dag.children = dag.children.filter(c => !n.inserted_roots.includes(c));
            // remove inserted connections
            n.inserted_connections.forEach(
                arr => {
                    // check existence to prevent double entries
                    // which will cause crashes
                    if (arr[0].children.includes(arr[1])) {
                        arr[0]._children.push(arr[1]);
                        arr[0].children.remove(arr[1]);
                    }
                }
            )
            // repeat for all inserted nodes
            n.inserted_nodes.forEach(remove_inserted_root_nodes);
        };
        remove_inserted_root_nodes(d);

        // collapse neighbors which are visible and have been inserted by this node
        var vis_inserted_neighbors = d.neighbors.filter(n => n.visible & d.inserted_nodes.includes(n));
        vis_inserted_neighbors.forEach(
            n => {
                // tag invisible
                n.visible = false;
                // if child, delete connection
                if (d.children.includes(n)) {
                    d._children.push(n);
                    d.children.remove(n);
                }
                // if parent, delete connection
                if (n.children.includes(d)) {
                    n._children.push(d);
                    n.children.remove(d);
                }
                // if union, collapse the union
                if (n.data.isUnion) {
                    collapse(n);
                }
                // remove neighbor handle from clicked node
                d.inserted_nodes.remove(n);
            }
        );
        d._collapsed = true
    }*/

    // uncollapse a node
    function uncollapse(d, make_roots) {
        //console.log("uncollapse ---> d:",d)
        if (d === undefined) return;

        // neighbor nodes that are already visible (happens when
        // circles occur): make connections, save them to
        // destroy / rebuild on collapse
        var extended_neighbors = d.neighbors.filter(n => n.visible)
        extended_neighbors.forEach(
            n => {
                // if child, make connection
                if (d._children.includes(n)) {
                    d.inserted_connections.push([d, n]);
                }
                // if parent, make connection
                if (n._children.includes(d)) {
                    d.inserted_connections.push([n, d]);
                }
            }
        )

        // neighbor nodes that are invisible: make visible, make connections,
        // add root nodes, add to inserted_nodes
        var collapsed_neighbors = d.neighbors.filter(n => !n.visible);
        collapsed_neighbors.forEach(
            n => {
                // collect neighbor data
                n.neighbors = getNeighbors(n);
                // tag visible
                n.visible = true;
                // if child, make connection
                if (d._children.includes(n)) {
                    d.children.push(n);
                    d._children.remove(n);
                }
                // if parent, make connection
                if (n._children.includes(d)) {
                    n.children.push(d);
                    n._children.remove(d);
                    // insert root nodes if flag is set
                    if (make_roots && !d.inserted_roots.includes(n)) {
                        d.inserted_roots.push(n);
                    }
                }

                // save neighbor handle in clicked node
                d.inserted_nodes.push(n);
            }
        )

        // make sure this step is done only once
        if (!make_roots) {
            var add_root_nodes = n => {
                // add previously inserted root nodes (partners, parents)
                n.inserted_roots.forEach(p => dag.children.push(p));
                // add previously inserted connections (circles)
                n.inserted_connections.forEach(
                    arr => {
                        // check existence to prevent double entries
                        // which will cause crashes
                        if (arr[0]._children.includes(arr[1])) {
                            arr[0].children.push(arr[1]);
                            arr[0]._children.remove(arr[1]);
                        }
                    }
                )
                // repeat with all inserted nodes
                n.inserted_nodes.forEach(add_root_nodes)
            };
            add_root_nodes(d);
        }
        d._collapsed = false
    }

    /**function is_extendable(d) {
        //console.log("clicked d: ", d)
        return d.neighbors.length > 0 && d._collapsed
    }*/

    function getNeighbors(node) {
        return getChildren(node)
    }

    function getChildren(node) {
        var children = [];
        children = node.children.concat(node._children);
        // filter undefined
        children = children
            .filter(c => c !== undefined)
        return children
    }

    function update(source) {
        //console.log("update --> source:",source)
        // Assigns the x and y position for the nodes
        var dag_tree = tree(dag),
            nodes = dag_tree.descendants(),
            links = dag_tree.links();

        // console.log("update ---> nodes: ", nodes)
        handleSelection(nodes)

        //console.log("dag_tree: ", dag_tree)
        //console.log("dag_tree.links(): ", links)
        //console.log("dag_tree.descendants(): ", nodes)

        // ****************** Nodes section ***************************

        // Update the nodes...
        var node = g.selectAll('g.node')
            .data(nodes, function (d) {
                return d.id || (d.id = ++i);
            })
        //console.log("----> node:",node)
        // Enter any new nodes at the parent's previous position.
        var nodeEnter = node.enter().append('g')
            .attr('class', 'node')
            .attr("transform", function (d) {
                return "translate(" + source.y0 + "," + source.x0 + ")";
            })
            .attr('display', function (d) {
                if (d.id === "Roots")
                    return 'none'
            })

        // Add Box for the nodes
        nodeEnter.append('rect')
            .attr('class', 'node')
            //.attr('r', 1e-6)
            .attr("x", "-100")
            .attr("y", "-50")
            .attr('width', '250')
            .attr('height', '110')
            .attr('rx', '20')
            .on('click', (event, d) => clickIcons(event, d, 'eventDetails'))
            .style("fill", function (d) {
                return d._children.length > 0 ? primaryColor : "white";
            })
            .style("stroke", function (d) {
                return _.indexOf(selectedSgtins, extractSerialCode(d.id)) > -1 ? accentColor : primaryColor;
                //return _.indexOf(selectedSgtins, extractSerialCode(d.id)) > -1 ? "#f47560" : primaryColor;
                //return _.indexOf(selectedSgtins, d.id) > -1 ? "#e70054" : "#2593b8";
            })
            .style("stroke-width", function (d) {
                return _.indexOf(selectedSgtins, extractSerialCode(d.id)) > -1 ? 6 : 3;
            })

        // Add counters for truncated links
        /*nodeEnter.append('text')
            .attr("x", "-110")
            .attr("y", "0")
            .attr("text-anchor", "end")
            .attr('visible', function (d) {
                return (d.id !== "Roots")
            })
            .style('font-size', 'xxx-large')
            .text(function (d) {
                if(_.includes(d.data.parentIds, 'Roots') && (d.children.length === 0 || d._children.length === 0))
                    return ""
                return d.data.cnt
            })*/
        // Add counters for truncated links
        nodeEnter.append('rect')
            //.attr("cx", 0)
            //.attr("cy", 60)
            //.attr("r", 20)
            .attr("x", "-85")
            .attr("y", "42")
            .attr('width', '110')
            .attr('height', '40')
            .attr('rx', '8')
            .style("fill", primaryColor)
            .attr('cursor', 'pointer')
            .attr('visibility', function (d) {
                const cnt = d.data.outputProdClassCnt || d.data.inputProdClassCnt
                //const truncatedChildren = d.data.outputProdClassCnt - maxChildren
                const truncatedChildren = cnt - maxChildren
                return truncatedChildren > 0 && d.marker ? 'visible' : 'hidden'
            })
        nodeEnter.append('text')
            .attr("x", "-80")
            .attr("y", "61")
            .attr("text-anchor", "start")
            .attr('visible', function (d) {
                return (d.id !== "Roots")
            })
            .attr('fill', 'white')
            .style('font-size', 'large')
            .text(function (d) {
                const truncatedChildren = (d.data.outputProdClassCnt || d.data.inputProdClassCnt) - maxChildren
                if (truncatedChildren > 0 && d.marker) {
                    // if (_.includes(d.data.parentIds, 'Roots') && (d.children.length === 0 || d._children.length === 0))
                    //   return ""
                    return '+' + truncatedChildren + ' more'
                }
            })
        nodeEnter.append('text')
            .attr("x", "-80")
            .attr("y", "76")
            .attr("text-anchor", "start")
            .attr('visible', function (d) {
                return (d.id !== "Roots")
            })
            .attr('fill', 'white')
            .style('font-size', 'x-small')
            .text(function (d) {
                const truncatedChildren = (d.data.outputProdClassCnt || d.data.inputProdClassCnt) - maxChildren
                if (truncatedChildren > 0 && d.marker) {
                    // if (_.includes(d.data.parentIds, 'Roots') && (d.children.length === 0 || d._children.length === 0))
                    //   return ""
                    return gtinNameLUT[extractProductClassCode(d.id)] || i18next.t('entities.noNameFound')
                }
            })
            .call(wrap, 110); // wrap the text in <= 110 pixels
        nodeEnter.append('rect')
            .attr("x", "-85")
            .attr("y", "42")
            .attr('width', '110')
            .attr('height', '40')
            .attr('rx', '8')
            .style("fill", 'transparent')
            .attr('cursor', 'pointer')
            .on('click', (event, d) => clickIcons(event, d, 'allSerialDetail'))

        // Add names as node labels
        nodeEnter.append('text')
            .attr("dy", "26")
            //.attr("x", 0)
            .attr("x", "-90")
            .attr("y", "-26")
            .attr("text-anchor", "start")
            .attr('visible', function (d) {
                return (d.id !== "Roots")
            })
            .text(function (d) {
                console.log("gtinNameLUT:",gtinNameLUT[extractProductClassCode(d.id)])
                return gtinNameLUT[extractProductClassCode(d.id)] || i18next.t('entities.noNameFound')
                //return (d.id)
            })
            .call(wrap, 180); // wrap the text in <= 30 pixels

        // add gtin as node labels
        nodeEnter.append('text')
            .attr("dy", "58")
            //.attr("x", 0)
            .attr("x", "-90")
            .attr("y", "-50")
            .attr("text-anchor", "start")
            .text(
                function (d) {
                    const classCode = extractProductClassCode(d.id)
                    const [type, codeValue, domain] = findCodeTypeAndValue(classCode)
                    const label = (domain ? domain : type.type) + ': ' + codeValue
                    return (d.id !== "Roots" ? label : null)
                }
            );

        // add sgtins
        nodeEnter.append('text')
            .attr("dy", "75")
            //.attr("x", 0)
            .attr("x", "-90")
            .attr("y", "-50")
            .attr("text-anchor", "start")
            .attr('fill', 'gray')
            .text(
                function (d) {
                    return (d.id !== "Roots" ? 'Serial: ' + findCodeTypeAndValue(extractSerialCode(d.id))[1] : null)
                    //return (d.id !== "Roots" ? 'Serial: '+d.id : null)
                }
            );

        /**
         * Icona occhietto details
         * M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z
         * */
        /*const foldPath = "M 9.5 0 C 4.256 0 0 4.256 0 9.5 s 4.256 9.5 9.5 9.5 s 9.5 -4.256 9.5 -9.5 S 14.744 0 9.5 0 z M 5.738 6.498 C 5.738 6.498 5.738 6.498 5.738 6.498 c 0.152 -0.152 0.266 -0.266 0.418 -0.418 c 0.266 -0.266 0.532 -0.532 0.798 -0.798 c 0.304 -0.304 0.646 -0.646 0.95 -0.95 c 0.266 -0.266 0.532 -0.532 0.76 -0.76 c 0.114 -0.114 0.228 -0.228 0.342 -0.342 c 0.266 -0.266 0.722 -0.266 1.026 0 c 0 0 0 0 0 0 c 0.152 0.114 0.266 0.266 0.38 0.38 c 0.266 0.266 0.532 0.532 0.798 0.798 c 0.304 0.304 0.646 0.646 0.95 0.95 c 0.266 0.266 0.532 0.532 0.76 0.76 c 0.114 0.114 0.228 0.228 0.342 0.342 c 0.152 0.152 0.19 0.304 0.19 0.494 c 0 0.19 -0.076 0.38 -0.228 0.494 c -0.114 0.114 -0.342 0.228 -0.494 0.228 c -0.19 0 -0.38 -0.076 -0.494 -0.228 c 0 0 0 0 0 0 c -0.152 -0.114 -0.266 -0.266 -0.38 -0.38 c -0.266 -0.266 -0.532 -0.532 -0.798 -0.798 c -0.304 -0.304 -0.646 -0.646 -0.95 -0.95 C 9.88 5.168 9.69 4.94 9.5 4.75 c -0.228 0.228 -0.456 0.456 -0.722 0.722 c -0.304 0.304 -0.646 0.646 -0.95 0.95 c -0.266 0.266 -0.532 0.532 -0.76 0.76 c -0.114 0.114 -0.228 0.228 -0.342 0.342 c -0.152 0.152 -0.304 0.19 -0.494 0.228 c -0.19 0 -0.38 -0.076 -0.494 -0.228 c -0.114 -0.114 -0.228 -0.342 -0.228 -0.494 C 5.51 6.84 5.586 6.65 5.738 6.498 z M 13.262 12.502 C 13.262 12.502 13.262 12.502 13.262 12.502 c -0.152 0.152 -0.266 0.266 -0.418 0.418 c -0.266 0.266 -0.532 0.532 -0.798 0.798 c -0.304 0.304 -0.646 0.646 -0.95 0.95 c -0.266 0.266 -0.532 0.532 -0.76 0.76 c -0.114 0.114 -0.228 0.228 -0.342 0.342 c -0.266 0.266 -0.722 0.266 -1.026 0 c 0 0 0 0 0 0 c -0.152 -0.114 -0.266 -0.266 -0.38 -0.38 c -0.266 -0.266 -0.532 -0.532 -0.798 -0.798 c -0.304 -0.304 -0.646 -0.646 -0.95 -0.95 c -0.266 -0.266 -0.532 -0.532 -0.76 -0.76 c -0.114 -0.114 -0.228 -0.228 -0.342 -0.342 c -0.152 -0.152 -0.19 -0.304 -0.228 -0.494 c 0 -0.19 0.076 -0.38 0.228 -0.494 c 0.114 -0.114 0.342 -0.228 0.494 -0.228 c 0.19 0 0.38 0.076 0.494 0.228 c 0 0 0 0 0 0 c 0.152 0.114 0.266 0.266 0.38 0.38 c 0.266 0.266 0.532 0.532 0.798 0.798 c 0.304 0.304 0.646 0.646 0.95 0.95 c 0.19 0.19 0.418 0.418 0.608 0.608 c 0.228 -0.228 0.456 -0.456 0.722 -0.722 c 0.304 -0.304 0.646 -0.646 0.95 -0.95 c 0.266 -0.266 0.532 -0.532 0.76 -0.76 c 0.114 -0.114 0.228 -0.228 0.342 -0.342 c 0.152 -0.152 0.304 -0.19 0.494 -0.228 c 0.19 0 0.38 0.076 0.494 0.228 c 0.114 0.114 0.228 0.342 0.228 0.494 C 13.49 12.16 13.414 12.35 13.262 12.502 z"
        const unfoldPath = "M 9.5 0 C 4.256 0 0 4.256 0 9.5 s 4.256 9.5 9.5 9.5 s 9.5 -4.256 9.5 -9.5 S 14.744 0 9.5 0 z M 13.186 15.428 c -0.114 0.114 -0.342 0.19 -0.494 0.19 c -0.19 0 -0.38 -0.076 -0.494 -0.19 c 0 0 0 0 0 0 c -0.114 -0.114 -0.266 -0.266 -0.38 -0.38 c -0.266 -0.266 -0.532 -0.532 -0.798 -0.798 c -0.304 -0.304 -0.608 -0.608 -0.912 -0.912 c -0.19 -0.19 -0.38 -0.38 -0.57 -0.57 c -0.228 0.228 -0.456 0.456 -0.684 0.684 c -0.304 0.304 -0.608 0.608 -0.912 0.912 c -0.266 0.266 -0.494 0.494 -0.76 0.76 c -0.114 0.114 -0.228 0.228 -0.304 0.304 c -0.152 0.152 -0.304 0.19 -0.494 0.19 c -0.19 0 -0.38 -0.076 -0.494 -0.19 c -0.114 -0.114 -0.19 -0.342 -0.19 -0.494 c 0 -0.19 0.076 -0.38 0.19 -0.494 c 0 0 0 0 0 0 c 0.114 -0.114 0.266 -0.266 0.38 -0.38 c 0.266 -0.266 0.532 -0.532 0.798 -0.798 c 0.304 -0.304 0.608 -0.608 0.912 -0.912 c 0.266 -0.266 0.494 -0.494 0.76 -0.76 c 0.114 -0.114 0.228 -0.228 0.304 -0.304 c 0.266 -0.266 0.722 -0.266 0.988 0 c 0 0 0 0 0 0 c 0.114 0.114 0.266 0.266 0.38 0.38 c 0.266 0.266 0.532 0.532 0.798 0.798 c 0.304 0.304 0.608 0.608 0.912 0.912 c 0.266 0.266 0.494 0.494 0.76 0.76 c 0.114 0.114 0.228 0.228 0.304 0.304 c 0.152 0.152 0.19 0.304 0.19 0.494 C 13.414 15.124 13.338 15.276 13.186 15.428 z M 13.186 4.56 C 13.186 4.56 13.186 4.56 13.186 4.56 c -0.152 0.152 -0.266 0.266 -0.38 0.38 c -0.266 0.266 -0.532 0.532 -0.798 0.798 c -0.304 0.304 -0.608 0.608 -0.912 0.912 c -0.266 0.266 -0.494 0.494 -0.76 0.76 c -0.114 0.114 -0.228 0.228 -0.304 0.304 c -0.266 0.266 -0.722 0.266 -0.988 0 c 0 0 0 0 0 0 c -0.114 -0.114 -0.266 -0.266 -0.38 -0.38 c -0.266 -0.266 -0.532 -0.532 -0.798 -0.798 c -0.304 -0.304 -0.608 -0.608 -0.912 -0.912 c -0.266 -0.266 -0.494 -0.494 -0.76 -0.76 c -0.114 -0.114 -0.228 -0.228 -0.304 -0.304 c -0.152 -0.152 -0.19 -0.304 -0.19 -0.494 c 0 -0.19 0.076 -0.38 0.19 -0.494 c 0.114 -0.114 0.342 -0.19 0.494 -0.19 c 0.19 0 0.38 0.076 0.494 0.19 c 0 0 0 0 0 0 c 0.114 0.114 0.266 0.266 0.38 0.38 c 0.266 0.266 0.532 0.532 0.798 0.798 c 0.304 0.304 0.608 0.608 0.912 0.912 c 0.19 0.19 0.38 0.38 0.57 0.57 c 0.228 -0.228 0.456 -0.456 0.684 -0.684 c 0.304 -0.304 0.608 -0.608 0.912 -0.912 c 0.266 -0.266 0.494 -0.494 0.76 -0.76 c 0.114 -0.114 0.228 -0.228 0.304 -0.304 c 0.152 -0.152 0.304 -0.19 0.494 -0.19 c 0.19 0 0.38 0.076 0.494 0.19 c 0.114 0.114 0.19 0.342 0.19 0.494 C 13.414 4.256 13.338 4.446 13.186 4.56 z"
        //console.log("source: ", source)
        const eyeDetails = nodeEnter.append("svg")
            .attr("width", 200)
            .attr("height", 200)
        eyeDetails.append('circle')
            .attr("cx", 74)
            .attr("cy", 42)
            .attr("r", 8)
            .style("fill", 'white')
            .attr('cursor', 'pointer')
            .on('click', click)
        eyeDetails.append('path')
            .attr("d", function (d) {
                return (d.id !== "Roots" ? d._collapsed ? unfoldPath : foldPath : null)
            })
            .attr('id', 'eventDetails')
            .attr("transform", "translate(65,"+32+")")
            .attr("fill", "gray")
            .attr("stroke", "none")
            .attr('cursor', 'pointer')
            .on('click', click)
        //.on('mouseover', function(d) {
        //d3.select(this).attr("transform", "translate(0,"+70+")");
        //})
        //.on('mouseout', function(d) {})
        */
        const copyIconPath = "M3 13h2v-2H3v2zm0 4h2v-2H3v2zm2 4v-2H3c0 1.1.89 2 2 2zM3 9h2V7H3v2zm12 12h2v-2h-2v2zm4-18H9c-1.11 0-2 .9-2 2v10c0 1.1.89 2 2 2h10c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 12H9V5h10v10zm-8 6h2v-2h-2v2zm-4 0h2v-2H7v2z"
        //console.log("source: ", source)
        const copySerialCode = nodeEnter.append("svg")
            .attr("width", 200)
            .attr("height", 200)

        copySerialCode.append('circle')
            //.attr("cx", 47)
            //.attr("cy", 44)
            .attr("cx", 94)
            .attr("cy", 42)
            .attr("r", 12)
            .attr('id', 'copyIconBg')
            .style("fill", function (d) {
                return d._children.length > 0 ? chroma.scale([primaryColor, 'white'])(0.85) : "white";
            })
            .attr('cursor', 'pointer')
            .on('click', (event, d) => clickIcons(event, d, 'copySerialCode'))
        copySerialCode.append('path')
            .attr("d", function (d) {
                return (d.id !== "Roots" && copyIconPath)
            })
            //.attr("transform", "translate(38,"+33+")")
            .attr("transform", "translate(85," + 32 + ")")
            .attr("fill", "gray")
            .attr('cursor', 'pointer')
            .on('click', (event, d) => clickIcons(event, d, 'copySerialCode'))

        /**
         * Icona Lente di ingrandimento
         * M 15.5 14 h -0.79 l -0.28 -0.27 C 15.41 12.59 16 11.11 16 9.5 C 16 5.91 13.09 3 9.5 3 S 3 5.91 3 9.5 S 5.91 16 9.5 16 c 1.61 0 3.09 -0.59 4.23 -1.57 l 0.27 0.28 v 0.79 l 5 4.99 L 20.49 19 l -4.99 -5 z z
         * semplice: M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z
         *  */
        const searchIconPath = "M 10.944 6.912 v 1.152 c 0 0.2376 -0.1944 0.432 -0.432 0.432 h -2.016 v 2.016 c 0 0.2376 -0.1944 0.432 -0.432 0.432 h -1.152 c -0.2376 0 -0.432 -0.1944 -0.432 -0.432 v -2.016 h -2.016 c -0.2376 0 -0.432 -0.1944 -0.432 -0.432 v -1.152 c 0 -0.2376 0.1944 -0.432 0.432 -0.432 h 2.016 v -2.016 c 0 -0.2376 0.1944 -0.432 0.432 -0.432 h 1.152 c 0.2376 0 0.432 0.1944 0.432 0.432 v 2.016 h 2.016 c 0.2376 0 0.432 0.1944 0.432 0.432 z m 7.236 10.2492 L 17.1612 18.18 c -0.3384 0.3384 -0.8856 0.3384 -1.2204 0 L 12.348 14.5908 c -0.162 -0.162 -0.252 -0.3816 -0.252 -0.612 V 13.392 c -1.2708 0.9936 -2.8692 1.584 -4.608 1.584 C 3.3516 14.976 0 11.6244 0 7.488 S 3.3516 0 7.488 0 s 7.488 3.3516 7.488 7.488 c 0 1.7388 -0.5904 3.3372 -1.584 4.608 h 0.5868 c 0.2304 0 0.45 0.09 0.612 0.252 l 3.5892 3.5892 c 0.3348 0.3384 0.3348 0.8856 0 1.224 z M 12.384 7.488 c 0 -2.7072 -2.1888 -4.896 -4.896 -4.896 S 2.592 4.7808 2.592 7.488 s 2.1888 4.896 4.896 4.896 s 4.896 -2.1888 4.896 -4.896 z"
        //console.log("source: ", source)
        const newSearch = nodeEnter.append("svg")
            .attr("width", 200)
            .attr("height", 200)

        newSearch.append('circle')
            //.attr("cx", 47)
            //.attr("cy", 44)
            .attr("cx", 124)
            .attr("cy", 42)
            .attr("r", 12)
            .attr('id', 'newSearchBg')
            .style("fill", function (d) {
                return d._children.length > 0 ? chroma.scale([primaryColor, 'white'])(0.85) : "white";
            })
            .attr('cursor', 'pointer')
            .on('click', (event, d) => {
                return (d.id !== "Roots"
                && _.indexOf(selectedSgtins, extractSerialCode(d.id)) === -1
                 ? clickIcons(event, d, 'newSearch') : null)
            })
        newSearch.append('path')
            .attr("d", function (d) {
                return (d.id !== "Roots"
                && _.indexOf(selectedSgtins, extractSerialCode(d.id)) === -1
                    ? searchIconPath : null)
            })
            //.attr("transform", "translate(38,"+33+")")
            .attr("transform", "translate(115," + 32 + ")")
            .attr("fill", "gray")
            .attr('cursor', 'pointer')
            .on('click', (event, d) => clickIcons(event, d, 'newSearch'))
        //.on('mouseover', function(d) {
        //d3.select(this).attr("transform", "translate(0,"+70+")");
        /*d3.select(this).transition().duration(200).attrTween("transform", function(d, i, a) {
            return d3.interpolateString(a, 'scale(2)');
        })*/
        //})
        //.on('mouseout', function(d) {})


        // UPDATE
        var nodeUpdate = nodeEnter.merge(node);

        // Transition to the proper position for the node
        nodeUpdate.transition()
            .duration(duration)
            .attr("transform", function (d) {
                return "translate(" + d.y + "," + d.x + ")";
            });

        // Update the node attributes and style
        nodeUpdate.select('rect.node')
            .attr('r', d => _.indexOf(selectedSgtins, extractSerialCode(d.id)) > -1 ? 10 : 7)
            .style("fill", function (d) {
                return d._children.length > 0 ? chroma.scale([primaryColor, 'white'])(0.85) : "white";
            })
            .attr('cursor', 'pointer');

        // Update the node attributes and style
        /* PER ICONA/STATO COLLAPSED
            nodeUpdate.select('#eventDetails')
            .attr("d", function (d) {
                return (d.id !== "Roots" ? d._collapsed ? unfoldPath : foldPath : null)
            })
            .attr('cursor', 'pointer');*/

        nodeUpdate.select('#newSearchBg')
            .style("fill", function (d) {
                return d._children.length > 0 ? chroma.scale([primaryColor, 'white'])(0.85) : "white";
            })
            .attr('cursor', 'pointer');

        // Remove any exiting nodes
        var nodeExit = node.exit().transition()
            .duration(duration)
            .attr("transform", function (d) {
                return "translate(" + source.y + "," + source.x + ")";
            })
            .attr('visible', false)
            .remove();

        // On exit reduce the node circles size to 0
        nodeExit.select('circle')
            .attr('r', 1e-6);

        // On exit reduce the opacity of text labels
        nodeExit.select('text')
            .style('fill-opacity', 1e-6);

        // ****************** links section ***************************

        // Update the links...
        var link = g.selectAll('path.link')
            .data(links, function (d) {
                return d.source.id + d.target.id
            });

        // Enter any new links at the parent's previous position.
        var linkEnter = link.enter().insert('path', "g")
            .attr("class", "link")
            .attr('display', function (d) {
                if (d.source.id === "Roots")
                    return 'none'
            })
            .attr('d', function (d) {
                var o = {x: source.x0, y: source.y0}
                return diagonal(o, o)
            });

        // UPDATE
        var linkUpdate = linkEnter.merge(link);

        // Transition back to the parent element position
        linkUpdate.transition()
            .duration(duration)
            .attr('d', d => diagonal(d.source, d.target));

        // Remove any exiting links
        link.exit().transition()
            .duration(duration)
            .attr('d', function (d) {
                var o = {x: source.x, y: source.y}
                return diagonal(o, o)
            })
            .remove();

        // expanding a big subgraph moves the entire dag out of the window
        // to prevent this, cancel any transformations in y-direction
        svg.transition()
            .duration(duration)
            .call(
                zoom.transform,
                d3.zoomTransform(g.node()).translate(-(source.y - source.y0), -(source.x - source.x0)),
            );

        // Store the old positions for transition.
        nodes.forEach(function (d) {
            d.x0 = d.x;
            d.y0 = d.y;
        });

        // Creates a curved (diagonal) path from parent to the child nodes
        function diagonal(s, d) {

            const path = `M ${s.y} ${s.x}
            C ${(s.y + d.y) / 2} ${s.x},
              ${(s.y + d.y) / 2} ${d.x},
              ${d.y} ${d.x}`

            return path
        }

        /** Toggle unions, children, partners on click.
        function click(event, d) {
            // uncollapse if there are uncollapsed unions / children / partners
            if (is_extendable(d))
                uncollapse(d)
            // collapse if fully uncollapsed
            else
                collapse(d)
            update(d);
        }*/

        function clickIcons(event, data, action) {
            //console.log("detailss ---> action:", action)
            //console.log("detailss ---> data:", data)
            handleIconsClick({
                id: data.id,
                parentIds: data.data.parentIds,
                children: data.children,
                eventTime: data.data.eventTime
            }, action)
        }

    }
}

const CustomD3DagComponent = forwardRef(({
                                             data,
                                             events,
                                             _eventsMap,
                                             selectedSgtins,
                                             searchSgtin,
                                             setSelectedSgtins,
                                             gtinNameLUT,
                                             w,
                                             h
                                         }, ref) => {
    const {primaryColor, accentColor} = useContext(ThemeContext)
    const [state,] = useContext(ProductHistoryContext)
    const {eventsMap, maxChildren, gtinNameLut, historyDirections} = state
    console.log("eventsMap:",eventsMap)

    const descriptionElementRef = useRef(null);
    const [dialogState, setDialogState] = useState({
        open: false,
        eventsIds: null,
        allSerialDetails: null,
        loading: false
    })

    const [dagUncollapsedNodes, setDagUncollapsedNodes] = useState([])

    useEffect(() => {
        renderGraph(ref.current, data, selectedSgtins, maxChildren, gtinNameLut, w, h, handleIconsClick, handleSelection, primaryColor, accentColor)
    }, [data, selectedSgtins, gtinNameLut])


    const handleSelection = (data) => {
        const filteredData = data.filter(node => node.id !== 'Roots' && !node._collapsed)
        setDagUncollapsedNodes(filteredData)
    }

    const productCategories = useMemo(() => {
        let res = {}
        const visited = {}
        dagUncollapsedNodes.forEach((node) => {
            const serial = extractSerialCode(node.id)
            if (serial in visited)
                return

            visited[serial] = 1

            //const productName = gtinNameLut[extractProductClassCode(serial)]
            const classCode = extractProductClassCode(serial)
            if (res[classCode])
                res[classCode].push(serial)
            else
                res[classCode] = [serial]
        })
        return res
    }, [dagUncollapsedNodes])

    const handleIconsClick = async (node, action) => {
        switch (action) {
            case 'eventDetails':
                setDialogState({open: true, eventsIds: eventsMap[node.id], allSerialDetails: null})
                break
            case 'newSearch':
                searchSgtin([...selectedSgtins, extractSerialCode(node.id)])
                break
            case 'copySerialCode':
                navigator.clipboard.writeText(extractSerialCode(node.id).toString())
                    .then(r => alert(i18next.t('productHistory.menuLabels.copySerialCode')))
                break
            case 'allSerialDetail':
                setDialogState({...dialogState, loading: true})
                const serial = _.split(node.id, ',')[0]
                const prodClass = extractProductClassCode(serial)
                //const eventTime = node.eventTime
                //const inputId = node.parentIds
                //const outputId = _.map(node.children, (childObj) => (childObj.id))
                const url = "/api/capture/epcis_events/_class_serial_history/"
                const body = historyDirections !== 'all' ?
                    {whats: selectedSgtins, backward: historyDirections === 'backward'}
                    : {whats: selectedSgtins}
                await axios.post(url, body)
                    .then(res => {
                        setDialogState({
                            open: true,
                            allSerialDetails: {
                                relatedSerialCodes: res.data[prodClass] || [],
                                name: prodClass,
                            },
                            eventsIds: null,
                            loading: false
                        })
                    }).catch((error) => {
                        console.log("error fetching class serials:", error)
                        setDialogState({...dialogState, loading: false})
                    })
                break
            default:
                console.log("handleIconsClick action: ", action, " --> something wrong...")
        }

    };

    const handleClose = () => {
        setDialogState({open: false, eventsIds: [], allSerialDetails: null})
    };

    return (
        <div>
            <Backdrop style={{zIndex:1000}} open={dialogState.loading}><CustomLoader size={80}/></Backdrop>
            <Grid container style={{backgroundColor: 'rgba(250,250,250,0)'}}>
                <div ref={ref}>
                    <Dialog fullWidth={true} maxWidth={"lg"} open={dialogState.open} onClose={handleClose}
                            scroll='paper'>
                        <DialogContent ref={descriptionElementRef} tabIndex={-1}>
                            {
                                !dialogState.eventsIds
                                    || dialogState.eventsIds.length === 0 ?
                                    <Typography align={'center'} style={{color: 'darkgrey'}}>
                                        {i18next.t('productHistory.messages.noEventsFound', 'No events found')}
                                    </Typography>
                                    : <GraphEventDetail eventsIds={dialogState.eventsIds}
                                                        gtinNameLUT={gtinNameLut} /*glnPlaceLUT={glnPlaceLUT}*//>
                            }
                            {
                                dialogState.allSerialDetails ?
                                    <NodeDetail data={dialogState.allSerialDetails} searchSgtin={searchSgtin}/> : null
                            }
                        </DialogContent>
                    </Dialog>
                </div>
                <SgtinListDownloadComponent productCategories={productCategories}/>
            </Grid>
        </div>
    )
})
export default CustomD3DagComponent
